import { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useMediaQuery } from 'react-responsive';
import { FiMail, FiPhone, FiChevronDown, FiX } from 'react-icons/fi';
import { HiOutlineCalendar } from 'react-icons/hi';
import { FaLinkedin, FaTwitter, FaFacebook } from 'react-icons/fa';
import Logo from '../../assets/logo1.jpg';

const NavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isDesktop = useMediaQuery({ minWidth: 1025 });
  const isTablet = useMediaQuery({ minWidth: 769, maxWidth: 1024 });
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isSmallMobile = useMediaQuery({ maxWidth: 480 });

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setIsDropdownOpen(false);
      }
    };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        if (!e.target.closest('.navbar-toggle')) setIsOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && !e.target.closest('.services-dropdown-trigger')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleContactClick = useCallback(() => {
    navigate('/contact');
    setIsOpen(false);
  }, [navigate]);

  const toggleMobileMenu = useCallback(() => {
    setIsOpen(prev => !prev);
    if (!isOpen) setIsDropdownOpen(false);
  }, [isOpen]);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => !prev);
  }, []);

  const menuVariants = {
    closed: { x: "100%", transition: { type: "tween", duration: 0.25 } },
    open: { x: 0, transition: { type: "tween", duration: 0.25 } }
  };

  const overlayVariants = {
    closed: { opacity: 0 },
    open: { opacity: 1 }
  };

  const itemVariants = {
    closed: { opacity: 0, x: 20 },
    open: { opacity: 1, x: 0 }
  };

  const navItems = [
    { name: 'Accueil', path: '/' },
    { name: 'À propos', path: '/about' },
    { 
      name: 'Services', 
      path: '/services',
      dropdown: [
        { name: 'Droit des Sociétés', path: '/Services-Infos/Services1' },
        { name: 'Expertise Corporative', path: '/Services-Infos/Services2' },
        { name: 'Droit Financier', path: '/Services-Infos/Services3' },
        { name: 'Droit Minier', path: '/Services-Infos/Services4' },
        { name: 'Droit Environnement', path: '/Services-Infos/Services5' },
        { name: 'Droit des Assurances', path: '/Services-Infos/Services6' },
        { name: 'Contentieux Civil', path: '/Services-Infos/Services7' },
        { name: 'RSE', path: '/Services-Infos/Services8' },
      ]
    },
    { name: 'Notre Équipe', path: '/Team' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      <nav 
        className={`navbar ${scrolled ? 'scrolled' : ''}`}
        style={styles.navbar}
        role="navigation"
        aria-label="Navigation principale"
      >
        <div style={styles.navbarContainer}>
          <div style={styles.navbarInner}>
            {/* Logo */}
            <NavLink to="/" style={styles.navbarLogo} aria-label="Retour à l'accueil">
              <motion.div 
                style={styles.logoWrapper}
                whileHover={{ x: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <div style={styles.logoImageContainer}>
                  <img src={Logo} alt="Logo KMJ SCP" style={styles.logoImage} loading="eager" />
                </div>
                <div style={styles.logoText}>
                  <h3 style={styles.logoTitle}>KMJ-LAWFIRM</h3>
                  <p style={styles.logoSubtitle}>Avocats & Conseils Juridiques</p>
                </div>
              </motion.div>
            </NavLink>

            {/* Desktop Navigation */}
            {isDesktop && (
              <ul style={styles.navbarLinks} role="menubar">
                {navItems.map((item) => (
                  <li key={item.name} style={styles.navItem} role="none">
                    {item.dropdown ? (
                      <div 
                        ref={dropdownRef}
                        style={styles.dropdownContainer}
                        onMouseEnter={() => setIsDropdownOpen(true)}
                        onMouseLeave={() => setIsDropdownOpen(false)}
                      >
                        <div 
                          className="services-dropdown-trigger"
                          style={styles.dropdownTrigger}
                          role="button"
                          aria-haspopup="true"
                          aria-expanded={isDropdownOpen}
                        >
                          <NavLink
                            to={item.path}
                            style={styles.navLink}
                            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                            role="menuitem"
                          >
                            <span style={styles.navText}>{item.name}</span>
                            <FiChevronDown style={{
                              ...styles.dropdownIcon,
                              transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0)'
                            }} />
                          </NavLink>
                        </div>
                        
                        <AnimatePresence>
                          {isDropdownOpen && (
                            <motion.div
                              style={styles.dropdownMenu}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 10 }}
                              transition={{ duration: 0.2 }}
                              role="menu"
                            >
                              {item.dropdown.map((subItem) => (
                                <NavLink
                                  key={subItem.name}
                                  to={subItem.path}
                                  style={styles.dropdownItem}
                                  onClick={() => setIsDropdownOpen(false)}
                                  role="menuitem"
                                  className={({ isActive }) => isActive ? 'dropdown-item active' : 'dropdown-item'}
                                >
                                  {subItem.name}
                                </NavLink>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <NavLink
                        to={item.path}
                        style={styles.navLink}
                        className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                        role="menuitem"
                      >
                        <span style={styles.navText}>{item.name}</span>
                        <span style={styles.navUnderline} className="nav-underline"></span>
                      </NavLink>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* Contact Info & Mobile Toggle */}
            <div style={styles.navbarRight}>
              {isDesktop && (
                <div style={styles.contactInfo}>
                  <a href="tel:+243822764820" style={styles.contactLink}>
                    <div style={styles.contactItem}>
                      <FiPhone style={styles.contactIcon} />
                      <span style={styles.contactText}>+243 822 764 820</span>
                    </div>
                  </a>
                  <a href="mailto:contact@kjm-scp.com" style={styles.contactLink}>
                    <div style={styles.contactItem}>
                      <FiMail style={styles.contactIcon} />
                      <span style={styles.contactText}>contact@kjm-scp.com</span>
                    </div>
                  </a>
                </div>
              )}

              <motion.button
                style={styles.navbarToggle}
                className={`navbar-toggle ${isOpen ? 'open' : ''}`}
                onClick={toggleMobileMenu}
                aria-label={isOpen ? "Fermer le menu" : "Ouvrir le menu"}
                aria-expanded={isOpen}
                whileTap={{ scale: 0.95 }}
              >
                {isOpen ? (
                  <FiX style={styles.closeIcon} />
                ) : (
                  <>
                    <span style={styles.burgerLine} className="burger-line-1"></span>
                    <span style={styles.burgerLine} className="burger-line-2"></span>
                    <span style={styles.burgerLine} className="burger-line-3"></span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </div>

        {/* Mobile Overlay & Menu (Resté très similaire structurellement mais les styles changent) */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              style={styles.mobileOverlay}
              onClick={() => setIsOpen(false)}
              variants={overlayVariants}
              initial="closed"
              animate="open"
              exit="closed"
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={mobileMenuRef}
              style={{ ...styles.navbarMobile, width: isSmallMobile ? '100%' : '380px' }}
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              <div style={styles.mobileHeader}>
                <div style={styles.mobileHeaderTop}>
                  <NavLink to="/" style={styles.mobileLogo} onClick={() => setIsOpen(false)}>
                    <img src={Logo} alt="Logo KMJ SCP" style={styles.mobileLogoImage} />
                    <div>
                      <h3 style={styles.mobileLogoTitle}>KMJ SCP</h3>
                      <p style={styles.mobileLogoSubtitle}>Avocats & Conseils</p>
                    </div>
                  </NavLink>
                  <button style={styles.mobileCloseButton} onClick={() => setIsOpen(false)}>
                    <FiX style={styles.mobileCloseIcon} />
                  </button>
                </div>
              </div>
              
              <ul style={styles.navbarMobileLinks}>
                {navItems.map((item, index) => (
                  <motion.li key={item.name} style={styles.mobileNavItem} variants={itemVariants} transition={{ delay: index * 0.05, duration: 0.2 }}>
                    {item.dropdown ? (
                      <>
                        <div style={styles.mobileDropdownHeader} onClick={toggleDropdown}>
                          <span style={styles.mobileLinkText}>{item.name}</span>
                          <FiChevronDown style={{ ...styles.mobileDropdownIcon, transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
                        </div>
                        <AnimatePresence>
                          {isDropdownOpen && (
                            <motion.div
                              style={styles.mobileDropdownContent}
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              {item.dropdown.map((subItem) => (
                                <NavLink
                                  key={subItem.name}
                                  to={subItem.path}
                                  style={styles.mobileDropdownItem}
                                  onClick={() => { setIsOpen(false); setIsDropdownOpen(false); }}
                                  className={({ isActive }) => `mobile-dropdown-item ${isActive ? 'active' : ''}`}
                                >
                                  {subItem.name}
                                </NavLink>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <NavLink
                        to={item.path}
                        style={styles.mobileLink}
                        className={({ isActive }) => `mobile-link ${isActive ? 'active' : ''}`}
                        onClick={() => setIsOpen(false)}
                      >
                        <span style={styles.mobileLinkText}>{item.name}</span>
                      </NavLink>
                    )}
                  </motion.li>
                ))}
              </ul>
              
              <div style={styles.mobileFooter}>
                <motion.button style={styles.mobileContactButton} whileHover={{ backgroundColor: '#C1A68D', color: '#1A1A1A' }} whileTap={{ scale: 0.98 }} onClick={handleContactClick}>
                  <HiOutlineCalendar style={styles.mobileButtonIcon} />
                  <span>Prendre Rendez-vous</span>
                </motion.button>
                
                <div style={styles.mobileContactInfo}>
                  <a href="tel:+243822764820" style={styles.mobileContactLink}>
                    <div style={styles.mobileContactItem}>
                      <FiPhone style={styles.mobileContactIcon} />
                      <p style={styles.mobileContactText}>+243 822 764 820</p>
                    </div>
                  </a>
                  <a href="mailto:contact@kjm-scp.com" style={styles.mobileContactLink}>
                    <div style={styles.mobileContactItem}>
                      <FiMail style={styles.mobileContactIcon} />
                      <p style={styles.mobileContactText}>contact@kjm-scp.com</p>
                    </div>
                  </a>
                </div>
                
                <div style={styles.mobileSocial}>
                  <div style={styles.mobileSocialIcons}>
                    <a href="#" style={styles.mobileSocialIcon}><FaLinkedin /></a>
                    <a href="#" style={styles.mobileSocialIcon}><FaTwitter /></a>
                    <a href="#" style={styles.mobileSocialIcon}><FaFacebook /></a>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Styles globaux (CSS in JS) */}
      <style jsx>{`
        .navbar.scrolled {
          background: rgba(22, 22, 22, 0.98) !important;
          backdrop-filter: blur(15px) !important;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4) !important;
          border-bottom: 1px solid rgba(193, 166, 141, 0.1) !important;
        }
        
        .navbar-toggle.open .burger-line-1 {
          transform: rotate(45deg) translate(6px, 6px);
          background: #C1A68D !important;
        }
        
        .navbar-toggle.open .burger-line-2 {
          opacity: 0;
          transform: scaleX(0);
        }
        
        .navbar-toggle.open .burger-line-3 {
          transform: rotate(-45deg) translate(7px, -7px);
          background: #C1A68D !important;
        }
        
        .nav-link:hover .nav-text {
          color: #F5F0E6 !important;
        }

        .nav-link.active .nav-text {
          color: #F5F0E6 !important;
          font-weight: 500 !important;
        }
        
        .nav-link.active .nav-underline {
          width: 100% !important;
          background: #C1A68D !important;
        }
        
        .dropdown-item:hover {
          background: rgba(193, 166, 141, 0.08) !important;
          color: #F5F0E6 !important;
        }

        .dropdown-item.active {
          background: rgba(193, 166, 141, 0.1) !important;
          color: #F5F0E6 !important;
          border-left: 3px solid #C1A68D !important;
        }
        
        .mobile-link.active {
          background: rgba(193, 166, 141, 0.08) !important;
          color: #F5F0E6 !important;
          border-left-color: #C1A68D !important;
        }

        .mobile-dropdown-item.active {
          color: #C1A68D !important;
          border-left: 2px solid #C1A68D !important;
        }
        
        @media (max-width: 1024px) {
          .navbar-links, .contact-info { display: none !important; }
        }
        
        @media (max-width: 768px) {
          .navbar-inner { height: 70px !important; }
          .logo-title { font-size: 1rem !important; }
          .logo-subtitle { font-size: 0.7rem !important; }
          .logo-image { width: 40px !important; height: 40px !important; }
        }
      `}</style>
    </>
  );
};

const styles = {
  // Thème de couleurs principales:
  // Fond: #1A1A1A (Dark Gray)
  // Texte Principal: #F5F0E6 (Off-White Beige)
  // Texte Secondaire: #D4C5B0 (Muted Beige)
  // Accent/Highlight: #C1A68D (Gold/Taupe)

  navbar: {
    position: 'fixed',
    width: '100%',
    top: 0,
    zIndex: 1000,
    background: 'rgba(26, 26, 26, 0.85)',
    backdropFilter: 'blur(15px)',
    WebkitBackdropFilter: 'blur(15px)',
    borderBottom: '1px solid rgba(193, 166, 141, 0.08)',
    transition: 'all 0.3s ease',
  },
  navbarContainer: { maxWidth: '1400px', margin: '0 auto', padding: '0 1.5rem' },
  navbarInner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '85px' },
  navbarLogo: { display: 'flex', alignItems: 'center', textDecoration: 'none', minWidth: 0 },
  logoWrapper: { display: 'flex', alignItems: 'center', gap: '15px' },
  logoImageContainer: { position: 'relative', flexShrink: 0 },
  logoImage: {
    width: '50px',
    height: '50px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: '1px solid rgba(193, 166, 141, 0.4)',
    padding: '2px',
    background: '#242424',
  },
  logoText: { display: 'flex', flexDirection: 'column' },
  logoTitle: { margin: 0, fontSize: '1.2rem', fontWeight: 600, color: '#F5F0E6', letterSpacing: '1px', textTransform: 'uppercase' },
  logoSubtitle: { margin: '2px 0 0 0', fontSize: '0.75rem', color: '#C1A68D', fontWeight: 400, letterSpacing: '0.5px' },
  navbarLinks: { display: 'flex', gap: '2.5rem', listStyle: 'none', margin: 0, padding: 0 },
  navItem: { position: 'relative' },
  navLink: {
    position: 'relative',
    textDecoration: 'none',
    color: '#D4C5B0',
    fontWeight: 400,
    fontSize: '0.95rem',
    padding: '8px 0',
    transition: 'color 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    letterSpacing: '0.5px'
  },
  navText: { position: 'relative', zIndex: 1, padding: '0 2px', transition: 'color 0.3s ease' },
  navUnderline: {
    position: 'absolute',
    bottom: -2,
    left: 0,
    width: 0,
    height: '2px',
    background: '#C1A68D',
    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  dropdownContainer: { position: 'relative' },
  dropdownTrigger: { cursor: 'pointer', display: 'flex', alignItems: 'center' },
  dropdownIcon: { marginLeft: '6px', transition: 'transform 0.3s ease', fontSize: '1.1rem', color: '#C1A68D' },
  dropdownMenu: {
    position: 'absolute',
    top: 'calc(100% + 15px)',
    left: '50%',
    transform: 'translateX(-50%)',
    minWidth: '260px',
    background: 'rgba(26, 26, 26, 0.98)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(193, 166, 141, 0.15)',
    borderRadius: '4px',
    padding: '10px 0',
    boxShadow: '0 15px 35px rgba(0, 0, 0, 0.5)',
    zIndex: 100,
  },
  dropdownItem: {
    display: 'block',
    padding: '12px 24px',
    color: '#D4C5B0',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    fontSize: '0.9rem',
    borderLeft: '3px solid transparent',
  },
  navbarRight: { display: 'flex', alignItems: 'center', gap: '2rem' },
  contactInfo: { display: 'flex', flexDirection: 'column', gap: '8px', borderRight: '1px solid rgba(193, 166, 141, 0.2)', paddingRight: '2rem' },
  contactLink: { textDecoration: 'none' },
  contactItem: { display: 'flex', alignItems: 'center', gap: '10px' },
  contactIcon: { color: '#C1A68D', fontSize: '1rem' },
  contactText: { fontSize: '0.85rem', color: '#D4C5B0', fontWeight: 300, letterSpacing: '0.5px' },
  navbarToggle: {
    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
    width: '45px', height: '45px', background: 'transparent', border: '1px solid rgba(193, 166, 141, 0.3)',
    borderRadius: '4px', cursor: 'pointer', padding: 0, zIndex: 1100,
  },
  burgerLine: { display: 'block', width: '22px', height: '2px', background: '#D4C5B0', transition: 'all 0.3s ease', margin: '2.5px 0' },
  closeIcon: { color: '#C1A68D', fontSize: '1.6rem', transition: 'all 0.3s ease' },
  mobileOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(5px)', zIndex: 999 },
  navbarMobile: {
    position: 'fixed', top: 0, right: 0, height: '100vh', background: '#1A1A1A',
    boxShadow: '-10px 0 40px rgba(0, 0, 0, 0.7)', zIndex: 1001, display: 'flex', flexDirection: 'column',
    borderLeft: '1px solid rgba(193, 166, 141, 0.1)', overflow: 'hidden',
  },
  mobileHeader: { padding: '1.5rem', borderBottom: '1px solid rgba(193, 166, 141, 0.1)' },
  mobileHeaderTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' },
  mobileLogo: { display: 'flex', alignItems: 'center', gap: '15px', textDecoration: 'none' },
  mobileLogoImage: { width: '45px', height: '45px', borderRadius: '6px', border: '1px solid #C1A68D' },
  mobileLogoTitle: { margin: 0, fontSize: '1rem', color: '#F5F0E6', fontWeight: 600, textTransform: 'uppercase' },
  mobileLogoSubtitle: { margin: '2px 0 0 0', fontSize: '0.7rem', color: '#C1A68D' },
  mobileCloseButton: { background: 'transparent', border: 'none', color: '#D4C5B0', cursor: 'pointer', padding: '5px' },
  mobileCloseIcon: { fontSize: '1.5rem' },
  navbarMobileLinks: { listStyle: 'none', margin: 0, padding: '1rem 0', flex: 1, overflowY: 'auto' },
  mobileNavItem: { margin: 0 },
  mobileLink: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem',
    textDecoration: 'none', color: '#D4C5B0', fontSize: '1rem', fontWeight: 400, borderLeft: '4px solid transparent',
  },
  mobileLinkText: { display: 'flex', alignItems: 'center', gap: '15px' },
  mobileDropdownHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem',
    color: '#D4C5B0', fontSize: '1rem', fontWeight: 400, cursor: 'pointer', borderLeft: '4px solid transparent',
  },
  mobileDropdownIcon: { color: '#C1A68D', fontSize: '1.2rem', transition: 'transform 0.3s ease' },
  mobileDropdownContent: { overflow: 'hidden', background: '#161616' },
  mobileDropdownItem: {
    display: 'block', padding: '0.8rem 1.5rem 0.8rem 3rem', color: '#A39683', textDecoration: 'none',
    fontSize: '0.9rem', borderLeft: '2px solid transparent', transition: 'color 0.2s ease',
  },
  mobileFooter: { padding: '1.5rem', borderTop: '1px solid rgba(193, 166, 141, 0.1)', display: 'flex', flexDirection: 'column', gap: '1.5rem' },
  mobileContactButton: {
    background: 'transparent', color: '#C1A68D', border: '1px solid #C1A68D', padding: '1rem',
    borderRadius: '4px', fontWeight: 500, fontSize: '0.95rem', cursor: 'pointer', transition: 'all 0.3s ease',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', textTransform: 'uppercase', letterSpacing: '1px'
  },
  mobileButtonIcon: { fontSize: '1.2rem' },
  mobileContactInfo: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  mobileContactLink: { textDecoration: 'none' },
  mobileContactItem: { display: 'flex', alignItems: 'center', gap: '12px' },
  mobileContactIcon: { color: '#C1A68D', fontSize: '1.1rem' },
  mobileContactText: { color: '#D4C5B0', fontSize: '0.9rem', margin: 0 },
  mobileSocial: { marginTop: '10px' },
  mobileSocialIcons: { display: 'flex', gap: '1rem' },
  mobileSocialIcon: {
    width: '35px', height: '35px', borderRadius: '4px', background: 'rgba(193, 166, 141, 0.1)',
    color: '#C1A68D', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none',
    transition: 'all 0.3s ease', fontSize: '1rem',
  },
};

export default NavBar;